import { useContext, useEffect, useState } from "react";
import {
  Box, Button, MenuItem, FormControl, Grid, IconButton, InputLabel, Paper, Select,
  Stack, Table, TableBody, TableCell, Container, TableContainer, TableHead, TableRow,
  styled, tableCellClasses, Pagination, Typography,
  Divider, Backdrop, CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Checkbox, Autocomplete, createFilterOptions, TextField, SxProps, Theme
} from "@mui/material";
import { AlertType } from "../../../types/AlertType";
import { SnackBarAlert } from "../../element/SnackBarAlert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { tableStyle } from "../parametrage/Pays/Pays";
import RowTableauDeBordFacturation from "./RowTableauDeBordFacturation";
import DialogDateEnvoiFacture from "./DialogDateEnvoiFacture";
import DialogDateValidationFacture from "./DialogDateValidationFacture";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loggoutUser } from "../../../redux/actions/logginUser";
import { MediaQueryContext } from "../../../utils/contextes/MediaQueryContext";
import { DataRef } from "../../../types/DataRef";
import { ConnectedUser } from "../../../utils/utils";
import { PROFIL_ADV, PROFIL_SUPER_ADMIN } from "../../../constants/Constants";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    textAlign: "center",
    fontSize: 11,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    textAlign: "center",
    color: "#fff",
  },
}));

// Statuts renvoyés par facturation/dashboard : une ligne est validée seulement
// si toutes les saisies manager qui la composent le sont.
export const STATUT_VALIDEE = "Validée";
export const STATUT_NON_VALIDEE = "Non validée";
export const STATUT_ENVOYEE = "Envoyée";

export type StatutFacture = "Validée" | "Non validée" | "Envoyée";

// Profils autorisés à valider la facturation en masse depuis le tableau de bord
const profilsValidationEnMasse: string[] = [PROFIL_SUPER_ADMIN, PROFIL_ADV];

export const MOIS: { id: number; libelle: string }[] = [
  { id: 1, libelle: "Janvier" },
  { id: 2, libelle: "Février" },
  { id: 3, libelle: "Mars" },
  { id: 4, libelle: "Avril" },
  { id: 5, libelle: "Mai" },
  { id: 6, libelle: "Juin" },
  { id: 7, libelle: "Juillet" },
  { id: 8, libelle: "Août" },
  { id: 9, libelle: "Septembre" },
  { id: 10, libelle: "Octobre" },
  { id: 11, libelle: "Novembre" },
  { id: 12, libelle: "Décembre" },
];

/**
 * Liste des années proposées dans le filtre : 5 ans en arrière jusqu'à l'année prochaine
 */
export const ANNEES: number[] = (() => {
  const anneeEnCours = new Date().getFullYear();
  const annees: number[] = [];

  for (let annee = anneeEnCours + 1; annee >= anneeEnCours - 5; annee--) {
    annees.push(annee);
  }

  return annees;
})();

/**
 * @param mois
 * @param annee
 * Affiche la période de facturation sous la forme "Janvier 2026"
 */
export const formatPeriode = (mois: number, annee: number): string => {
  const libelle = MOIS.find((m) => m.id === Number(mois))?.libelle;

  return libelle ? `${libelle} ${annee}` : String(annee ?? "");
};

/**
 * @param date
 * Affiche une date renvoyée par le back ("2026-07-22" ou "2026-07-22 00:00:00")
 * au format "22-07-2026". Le découpage est fait sur la chaîne pour
 * éviter tout décalage de fuseau horaire.
 */
export const formatDateFr = (date: string | null): string => {
  if (!date) {
    return "-";
  }

  const [annee, mois, jour] = String(date).split(" ")[0].split("-");

  return annee && mois && jour ? `${jour}-${mois}-${annee}` : "-";
};

/**
 * @param caGlobalHt
 * Affiche le CA globale HT au format français avec 2 décimales
 */
export const formatCaGlobalHt = (caGlobalHt: string | number): string => {
  const montant = parseFloat(String(caGlobalHt));

  if (isNaN(montant)) {
    return "-";
  }

  return montant.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// La 1ère colonne (case à cocher) est rendue à part.
// Le back ne gère pas de tri paramétrable : l'ordre est figé (période, opération,
// société, code gesco décroissants), les entêtes ne sont donc pas cliquables.
const tableColumnTittles: string[] = [
  "Société de facturation",
  "BU",
  "Client",
  "Opération",
  "Code Gesco",
  "Période de facturation",
  "CA Globale HT",
  "Devise de facturation",
  "Statut",
  "Date de validation",
  "Date d'envoie de la facture",
];

/**
 * Ligne brute renvoyée par facturation/dashboard (cf. ServiceFacturation::formerDataEnDataDashBoard).
 * Une ligne = un mois de saisie x un piv_oper_socifact_codes x une devise de facturation.
 */
interface DashboardApiRow {
  piv_oper_socifact_codes_id: number;
  societe_facturation: string | null;
  client: string | null;
  operation: string | null;
  bu: string | null;
  code_gesco: string | null;
  intitule_tiers: string | null;
  periode_facturation: { mois: number; annee: number; libelle: string };
  ca_global_ht: number;
  devise_facturation: string | null;
  ca_global_ht_compta: number;
  devise_compta: string | null;
  taux: number | null;
  statut: StatutFacture;
  erreurs: string[];
  // Date d'envoi de la facture au client (table `facture`), null tant que la
  // facture n'a pas été déclarée envoyée.
  date_envoi: string | null;
  // Date de validation des saisies (historique_sm_statut), null tant que la
  // ligne n'est pas entièrement validée.
  date_validation: string | null;
}

export interface DataFacture {
  // Le back ne renvoie pas d'identifiant de ligne : on reconstitue côté front la
  // clé de regroupement qu'il utilise (période x piv_oper x devise de facturation).
  id: string;
  piv_oper_socifact_codes_id: number;
  societe_facturation_libelle: string | null;
  client_libelle: string | null;
  operation_libelle: string | null;
  bu_libelle: string | null;
  code_gesco_libelle: string | null;
  intitule_tiers: string | null;
  periode_mois: number;
  periode_annee: number;
  ca_global_ht: number;
  devise_libelle: string | null;
  ca_global_ht_compta: number;
  devise_compta: string | null;
  taux: number | null;
  statut: StatutFacture;
  erreurs: string[];
  // Date d'envoi de la facture au client, saisie via le bouton
  // « Facture envoyée au client ». Null tant qu'elle n'a pas été renseignée.
  date_envoi: string | null;
  // Date à laquelle les saisies de la ligne ont été validées (dernière
  // validation en date). Null si la ligne n'est pas entièrement validée.
  date_validation: string | null;
}

/** Entrée d'une liste de référence alimentant un filtre (société, code gesco) */
interface ReferenceItem {
  id: number;
  libelle: string;
}

const filterCodeGescoOptions = createFilterOptions<ReferenceItem>({
  limit: 100,
  stringify: (option) => option.libelle ?? "",
});

interface FilterParams {
  mois: string;
  annee: string;
  societefacturation: string;
  codeGesco: string;
  statut: string;
  bu: string;
}

const initialFilter: FilterParams = {
  // dateDebut / dateFin sont obligatoires côté back : on part de l'année en cours.
  mois: "",
  annee: String(new Date().getFullYear()),
  societefacturation: "",
  codeGesco: "",
  statut: "",
  bu: ""
};

const formatDateIso = (date: Date): string => {
  const mois = String(date.getMonth() + 1).padStart(2, "0");
  const jour = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${mois}-${jour}`;
};

/**
 * Traduit le filtre Mois / Année en plage de dates attendue par facturation/dashboard.
 * Sans mois, on prend l'année entière.
 */
export const periodeToPlageDates = (
  filter: FilterParams
): { dateDebut: string; dateFin: string } => {
  const annee = filter?.annee
    ? Number(filter.annee)
    : new Date().getFullYear();

  if (filter?.mois) {
    const mois = Number(filter.mois);

    return {
      // Le 0e jour du mois suivant = dernier jour du mois demandé
      dateDebut: formatDateIso(new Date(annee, mois - 1, 1)),
      dateFin: formatDateIso(new Date(annee, mois, 0)),
    };
  }

  return {
    dateDebut: `${annee}-01-01`,
    dateFin: `${annee}-12-31`,
  };
};

const mapFacture = (row: DashboardApiRow): DataFacture => {
  const mois = Number(row?.periode_facturation?.mois);
  const annee = Number(row?.periode_facturation?.annee);

  return {
    id: `${row?.piv_oper_socifact_codes_id}-${annee}-${mois}-${row?.devise_facturation ?? ""}`,
    piv_oper_socifact_codes_id: row?.piv_oper_socifact_codes_id,
    societe_facturation_libelle: row?.societe_facturation,
    client_libelle: row?.client,
    operation_libelle: row?.operation,
    bu_libelle: row?.bu,
    code_gesco_libelle: row?.code_gesco,
    intitule_tiers: row?.intitule_tiers,
    periode_mois: mois,
    periode_annee: annee,
    ca_global_ht: row?.ca_global_ht,
    devise_libelle: row?.devise_facturation,
    ca_global_ht_compta: row?.ca_global_ht_compta,
    devise_compta: row?.devise_compta,
    taux: row?.taux,
    statut: row?.statut,
    erreurs: Array.isArray(row?.erreurs) ? row.erreurs : [],
    date_envoi: row?.date_envoi ?? null,
    date_validation: row?.date_validation ?? null,
  };
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const TableauDeBordFacturation = () => {
  const { isDesktopOrLaptop } = useContext(MediaQueryContext);

  const user = ConnectedUser();
  const peutValiderEnMasse: boolean = profilsValidationEnMasse.includes(
    user?.profils?.libelle
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSnack, setOpenSnack] = useState<boolean>(false);
  const [reloadList, setReloadList] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>(false);

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [totalData, setTotalData] = useState<number>(0);
  const [filter, setFilter] = useState<FilterParams>(initialFilter);

  const [alertMessage, setAlerteMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<AlertType>("success");

  const [factures, setFactures] = useState<DataFacture[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openDialogDateEnvoi, setOpenDialogDateEnvoi] = useState<boolean>(false);
  // Lignes dont la date de validation est en cours d'ajout / de modification :
  // une seule via le crayon, plusieurs via « Valider la facturation ».
  const [facturesDateValidation, setFacturesDateValidation] =
    useState<DataFacture[]>([]);

  const [societesFacturation, setSocietesFacturation] = useState<ReferenceItem[]>([]);
  const [businessUnitdatas, setBusinessUnitdatas] = useState<DataRef[]>([]);
  const [codeGescos, setCodeGescos] = useState<ReferenceItem[]>([]);

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlBaseApi,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Add global response interceptor
  http.interceptors.response.use(
    (response) => {
      // Modify response data here, if needed
      return response;
    },
    (error) => {
      console.log(error.message);
      if (error.message === "Request failed with status code 401") {
        // Remove the redux store
        dispatch(loggoutUser());

        // Remove token in navigator cookies
        cookies.remove("jwt", { path: "/" });

        navigate("/sign-in");
      }
      return Promise.reject(error);
    }
  );

  const retrieveData = () => {
    // Récupère les factures consolidées du tableau de bord
    setIsLoading(true);

    const { dateDebut, dateFin } = periodeToPlageDates(filter);

    const data = {
      dateDebut,
      dateFin,
      // Le back ignore ces 3 filtres quand ils sont vides (empty())
      business_unit: filter?.bu,
      societefacturation: filter?.societefacturation,
      codeGesco: filter?.codeGesco,
      statut: filter?.statut,
      rowsPerPage,
      page,
    };

    http
      .post("facturation/dashboard", data)
      .then((res) => {
        // Le back renvoie un paginateur Laravel : { data, total, current_page, ... }
        const { total, data } = res.data;

        setTotalData(total ?? 0);
        setFactures(Array.isArray(data) ? data.map(mapFacture) : []);
      })
      .catch((error) => {
        console.log(error);
        setAlerteMessage("Désolé! Une erreur est survenue !");
        setAlertType("error");
        setOpenSnack(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // La sélection ne doit pas survivre à un changement de liste
    setSelectedIds([]);
    retrieveData();
  }, [filter, rowsPerPage, page, reloadList]);

  useEffect(() => {
    // Récuperation des société de facturation proposées dans le filtre
    http
      .get("societe-facturation/find/all")
      .then((res) => {
        Array.isArray(res.data) && setSocietesFacturation(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    // Liste des business units proposées dans le filtre
    if (!filter?.societefacturation) {
      setSocietesFacturation([]);

      return;
    }

    http
      .get(`bu/find-by/societe-facturation/${filter?.societefacturation}`)
      .then((res) => {
        Array.isArray(res.data) && setBusinessUnitdatas(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [filter?.societefacturation]);

  useEffect(() => {
    // Les codes gesco dépendent de la société de facturation choisie
    if (!filter?.societefacturation) {
      setCodeGescos([]);

      return;
    }

    http
      .get(`code-gesco/findby/societe-facturation/${filter.societefacturation}`)
      .then((res) => {
        Array.isArray(res.data) && setCodeGescos(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [filter?.societefacturation]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setPage(1);
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const handleChangeBusinessUnit = (e: any) => {
    const { value } = e.target;

    setPage(1);
    // Le code gesco dépend de la société : il ne doit pas lui survivre
    setFilter({
      ...filter,
      bu: value,
      codeGesco: "",
    });
  };

  const handleChangeSocieteFacturation = (e: any) => {
    const { value } = e.target;

    setPage(1);
    // Le code gesco dépend de la société : il ne doit pas lui survivre
    setFilter({
      ...filter,
      societefacturation: value,
      bu: "",
      codeGesco: "",
    });
  };

  const resetPage = () => {
    window.location.reload();
  };

  /**
   * Seules les factures validées et pas encore envoyées peuvent être déclarées
   * envoyées au client.
   */
  const estEnvoyable = (facture: DataFacture): boolean =>
    facture?.statut === STATUT_VALIDEE && !facture?.date_envoi;

  /**
   * Les profils autorisés (ADV, super admin) peuvent valider en masse les
   * factures encore non validées.
   */
  const estValidable = (facture: DataFacture): boolean =>
    peutValiderEnMasse && facture?.statut === STATUT_NON_VALIDEE;

  // Une ligne est cochable dès qu'une des 2 actions en masse la concerne
  // (même règle que la case à cocher de chaque ligne, cf. RowTableauDeBordFacturation).
  const estSelectionnable = (facture: DataFacture): boolean =>
    estEnvoyable(facture) || estValidable(facture);

  const facturesSelectionnables: DataFacture[] =
    factures.filter(estSelectionnable);

  const facturesSelectionnees: DataFacture[] = factures.filter((facture) =>
    selectedIds.includes(facture?.id)
  );

  // Chaque bouton n'agit que sur les lignes cochées qui le concernent : une
  // sélection mixte valide les non validées et envoie les validées séparément.
  const facturesAEnvoyer: DataFacture[] =
    facturesSelectionnees.filter(estEnvoyable);
  const facturesAValider: DataFacture[] =
    facturesSelectionnees.filter(estValidable);

  const allSelected: boolean =
    facturesSelectionnables.length > 0 &&
    selectedIds.length === facturesSelectionnables.length;

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedIds(
      allSelected ? [] : facturesSelectionnables.map((facture) => facture?.id)
    );
  };

  const handleCloseAlert = () => {
    setOpenSnack(false);
  };

  const actionEnMasseButtonSx: SxProps<Theme> = {
    textTransform: "none",
    mb: { xs: 2 },
    // Etat désactivé (aucune ligne concernée cochée) : bouton visible mais grisé.
    "&.Mui-disabled": {
      backgroundColor: "#9e9e9e",
      color: "#e0e0e0",
    },
  };

  // Ouvre le pop-up de saisie de la date de validation pour les lignes
  // cochées non validées (réservé aux profils autorisés).
  const validerFacturationButton: JSX.Element | false = peutValiderEnMasse && (
    <Button
      variant="contained"
      startIcon={<DoneAllIcon />}
      color="success"
      fullWidth={!isDesktopOrLaptop}
      size={isDesktopOrLaptop ? "medium" : "small"}
      sx={actionEnMasseButtonSx}
      disabled={facturesAValider.length === 0}
      onClick={() => setFacturesDateValidation(facturesAValider)}
    >
      Valider la facturation
    </Button>
  );

  // Ouvre le pop-up de saisie de la date d'envoi pour les lignes cochées.
  const factureEnvoyeeButton: JSX.Element = (
    <Button
      variant="contained"
      startIcon={<SendIcon />}
      color="warning"
      fullWidth={!isDesktopOrLaptop}
      size={isDesktopOrLaptop ? "medium" : "small"}
      sx={actionEnMasseButtonSx}
      disabled={facturesAEnvoyer.length === 0}
      onClick={() => setOpenDialogDateEnvoi(true)}
    >
      Facture envoyée au client
    </Button>
  );

  // Les 2 actions en masse côte à côte (validation, puis envoi au client).
  const actionsEnMasse: JSX.Element = (
    <Stack direction="row" spacing={isDesktopOrLaptop ? 2 : 1}>
      {validerFacturationButton}
      {factureEnvoyeeButton}
    </Stack>
  );

  const pagination: JSX.Element | boolean = (
    <Stack spacing={2} direction="row">
      <InputLabel sx={{ color: "#fff" }}>Lignes par page :</InputLabel>
      <FormControl variant="standard" sx={{ minWidth: 50 }}>
        <Select
          labelId="demo-simple-select-standard-label"
          id="demo-simple-select-standard"
          color="warning"
          sx={{ background: "#fff", borderRadius: 1, paddingLeft: 1 }}
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(e.target.value as number)}
          defaultValue={5}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>
      {!isLoading && totalData > rowsPerPage && (
        <Pagination
          count={Math.ceil(totalData / rowsPerPage)}
          color="standard"
          sx={{ button: { color: "white" } }}
          showFirstButton={true}
          showLastButton={true}
          siblingCount={0}
          boundaryCount={1}
          defaultPage={page}
          onChange={(event, value) => setPage(value)}
        />
      )}
    </Stack>
  );

  const stackAlert: JSX.Element = SnackBarAlert(
    openSnack,
    handleCloseAlert,
    alertType,
    alertMessage
  );

  return (
    <>
      <Container maxWidth="xl">
        {isDesktopOrLaptop ? (
          <>
            {/* Les 5 filtres sont alignés sur une seule ligne, à largeur égale :
                la grille est en 15 colonnes pour que 5 x md=3 tombent juste. */}
            <Grid
              container
              spacing={0.5}
              columns={{ xs: 6, sm: 10, md: 12 }}
              sx={{ mt: 1 }}
            >
              <Grid item xs={3} sm={5} md={2}>
                <FormControl fullWidth>
                  <InputLabel
                    id="societefacturation"
                    color="warning"
                    sx={{ mt: -1 }}
                  >
                    Société de facturation
                  </InputLabel>
                  <Select
                    labelId="societefacturation"
                    id="demo-simple-select"
                    name="societefacturation"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Société de facturation"
                    value={filter?.societefacturation}
                    onChange={handleChangeSocieteFacturation}
                  >
                    <MenuItem value="" dense></MenuItem>
                    {societesFacturation.map((societe) => (
                      <MenuItem key={societe?.id} value={societe?.id} dense>
                        {societe?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sm={5} md={2}>
                <FormControl fullWidth>
                  <InputLabel
                    id="bu"
                    color="warning"
                    sx={{ mt: -1 }}
                  >
                    Business unit
                  </InputLabel>
                  <Select
                    labelId="bu"
                    id="demo-simple-select"
                    name="bu"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Business unit"
                    value={filter?.bu}
                    onChange={handleChangeBusinessUnit}
                  >
                    <MenuItem value="" dense></MenuItem>
                    {businessUnitdatas.map((bu) => (
                      <MenuItem key={bu?.id} value={bu?.id} dense>
                        {bu?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sm={5} md={2}>
                <Autocomplete
                  size="small"
                  disabled={!filter?.societefacturation}
                  options={codeGescos}
                  getOptionLabel={(option) => option?.libelle || ""}
                  filterOptions={filterCodeGescoOptions}
                  value={
                    codeGescos.find(
                      (c) => String(c.id) === String(filter?.codeGesco)
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    setPage(1);
                    setFilter((prev) => ({
                      ...prev,
                      codeGesco: newValue ? String(newValue.id) : "",
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Code Gesco"
                      color="warning"
                      size="small"
                      sx={{ background: "#fff", borderRadius: 1 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3} sm={5} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="statut" color="warning" sx={{ mt: -1 }}>
                    Statut
                  </InputLabel>
                  <Select
                    labelId="statut"
                    id="demo-simple-select"
                    name="statut"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Statut"
                    value={filter?.statut}
                    onChange={handleChange}
                  >
                    <MenuItem value="" dense></MenuItem>
                    <MenuItem value={STATUT_VALIDEE} dense>
                      {STATUT_VALIDEE}
                    </MenuItem>
                    <MenuItem value={STATUT_NON_VALIDEE} dense>
                      {STATUT_NON_VALIDEE}
                    </MenuItem>
                    <MenuItem value={STATUT_ENVOYEE} dense>
                      {STATUT_ENVOYEE}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sm={5} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="mois" color="warning" sx={{ mt: -1 }}>
                    Mois
                  </InputLabel>
                  <Select
                    labelId="mois"
                    id="demo-simple-select"
                    name="mois"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Mois"
                    value={filter?.mois}
                    onChange={handleChange}
                  >
                    <MenuItem value="" dense></MenuItem>
                    {MOIS.map((mois) => (
                      <MenuItem key={mois?.id} value={mois?.id} dense>
                        {mois?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sm={5} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="annee" color="warning" sx={{ mt: -1 }}>
                    Année
                  </InputLabel>
                  <Select
                    labelId="annee"
                    id="demo-simple-select"
                    name="annee"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Année"
                    value={filter?.annee}
                    onChange={handleChange}
                  >
                    {ANNEES.map((annee) => (
                      <MenuItem key={annee} value={annee} dense>
                        {annee}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={0.5}
              columns={{ xs: 6, sm: 12, md: 12 }}
              sx={{ mt: 3 }}
            >
              <Grid item xs={6} sm={12} md={6}>
                <Box mt={2}>{pagination}</Box>
              </Grid>
              {/* Colonne élargie : elle porte les 2 boutons d'action en masse. */}
              <Grid item xs={6} sm={12} md={6}>
                <Stack
                  direction="row"
                  spacing={3}
                  display="flex"
                  alignItems="center"
                  ml={3}
                  justifyContent="end"
                  mt={{ sm: 2, xs: 2 }}
                >
                  {/* Pagination à gauche => boutons à droite, sur la même ligne. */}
                  {actionsEnMasse}
                  <IconButton
                    aria-label="refresh"
                    sx={{ color: "#fff", pt: 0.5 }}
                    onClick={() => resetPage()}
                  >
                    <RefreshIcon sx={{ fontSize: 35 }} />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>

            <TableContainer component={Paper} style={tableStyle}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <Checkbox
                        size="small"
                        color="warning"
                        sx={{ color: "#fff" }}
                        checked={allSelected}
                        indeterminate={
                          selectedIds.length > 0 && !allSelected
                        }
                        disabled={facturesSelectionnables.length === 0}
                        onChange={() => handleToggleSelectAll()}
                      />
                    </StyledTableCell>
                    {tableColumnTittles.map((tittle) => (
                      <StyledTableCell key={tittle}>{tittle}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading &&
                    (totalData > 0 ? (
                      factures.map((facture) => (
                        <RowTableauDeBordFacturation
                          key={facture?.id}
                          isDesktopOrLaptop={isDesktopOrLaptop}
                          facture={facture}
                          selectable={estSelectionnable(facture)}
                          selected={selectedIds.includes(facture?.id)}
                          onToggleSelect={handleToggleSelect}
                          onEditDateValidation={(f) => setFacturesDateValidation([f])}
                          expanded={expanded}
                          setExpanded={setExpanded}
                        />
                      ))
                    ) : (
                      <StyledTableCell colSpan={12}>
                        Aucun resultat a été trouvé ...
                      </StyledTableCell>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination à droite => boutons à gauche, sur la même ligne. */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mt={4}
              mb={6}
            >
              {actionsEnMasse}
              {pagination}
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                position: "fixed",
                width: "95%",
                top: "10.32rem",
                pr: 1.5,
                pt: 2,
                bgcolor: "#080d24",
                zIndex: 1000,
              }}
            >
              {actionsEnMasse}

              <Accordion>
                <AccordionSummary
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  Filtre
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel
                      id="societefacturation"
                      color="warning"
                      sx={{ mt: -1 }}
                    >
                      Société de facturation
                    </InputLabel>
                    <Select
                      labelId="societefacturation"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Société de facturation"
                      name="societefacturation"
                      value={filter?.societefacturation}
                      onChange={handleChangeSocieteFacturation}
                    >
                      <MenuItem value="" dense></MenuItem>
                      {societesFacturation.map((societe) => (
                        <MenuItem key={societe?.id} value={societe?.id} dense>
                          {societe?.libelle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ mt: 0.1 }}>
                    <Autocomplete
                      size="small"
                      disabled={!filter?.societefacturation}
                      options={codeGescos}
                      getOptionLabel={(option) => option?.libelle || ""}
                      filterOptions={filterCodeGescoOptions}
                      value={
                        codeGescos.find(
                          (c) => String(c.id) === String(filter?.codeGesco)
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setPage(1);
                        setFilter((prev) => ({
                          ...prev,
                          codeGesco: newValue ? String(newValue.id) : "",
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Code Gesco"
                          color="warning"
                          size="small"
                          sx={{ background: "#fff", borderRadius: 1 }}
                        />
                      )}
                    />
                  </Box>

                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel id="statut" color="warning" sx={{ mt: -1 }}>
                      Statut
                    </InputLabel>
                    <Select
                      labelId="statut"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Statut"
                      name="statut"
                      value={filter?.statut}
                      onChange={handleChange}
                    >
                      <MenuItem value="" dense></MenuItem>
                      <MenuItem value={STATUT_VALIDEE} dense>
                        {STATUT_VALIDEE}
                      </MenuItem>
                      <MenuItem value={STATUT_NON_VALIDEE} dense>
                        {STATUT_NON_VALIDEE}
                      </MenuItem>
                      <MenuItem value={STATUT_ENVOYEE} dense>
                        {STATUT_ENVOYEE}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ mt: 2.5 }} fullWidth>
                    <InputLabel id="mois" color="warning" sx={{ mt: -1 }}>
                      Mois
                    </InputLabel>
                    <Select
                      labelId="mois"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Mois"
                      name="mois"
                      value={filter?.mois}
                      onChange={handleChange}
                    >
                      <MenuItem value="" dense></MenuItem>
                      {MOIS.map((mois) => (
                        <MenuItem key={mois?.id} value={mois?.id} dense>
                          {mois?.libelle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel id="annee" color="warning" sx={{ mt: -1 }}>
                      Année
                    </InputLabel>
                    <Select
                      labelId="annee"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Année"
                      name="annee"
                      value={filter?.annee}
                      onChange={handleChange}
                    >
                      {ANNEES.map((annee) => (
                        <MenuItem key={annee} value={annee} dense>
                          {annee}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <Typography>Tableau de bord facturation</Typography>
                <IconButton
                  aria-label="refresh"
                  sx={{ color: "#fff" }}
                  onClick={() => resetPage()}
                >
                  <RefreshIcon />
                </IconButton>
              </Stack>
              <Divider orientation="horizontal" sx={{ bgcolor: "#fff", mb: 2 }} />

              {!isLoading && totalData > rowsPerPage && (
                <Box display="flex" justifyContent="end">
                  <Pagination
                    count={Math.ceil(totalData / rowsPerPage)}
                    size="small"
                    color="standard"
                    sx={{ button: { color: "white" }, mb: 2 }}
                    showFirstButton={true}
                    showLastButton={true}
                    siblingCount={0}
                    boundaryCount={1}
                    defaultPage={page}
                    onChange={(event, value) => setPage(value)}
                  />
                </Box>
              )}
            </Box>

            <Box mt="13rem">
              {factures.map((facture) => (
                <RowTableauDeBordFacturation
                  key={facture?.id}
                  isDesktopOrLaptop={isDesktopOrLaptop}
                  facture={facture}
                  selectable={estSelectionnable(facture)}
                  selected={selectedIds.includes(facture?.id)}
                  onToggleSelect={handleToggleSelect}
                  onEditDateValidation={(f) => setFacturesDateValidation([f])}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              ))}
            </Box>

            <Stack direction="row" sx={{ mt: 3, mb: 4 }}>
              <InputLabel sx={{ color: "#fff", fontSize: 12, mt: 0.5 }}>
                Lignes par page :
              </InputLabel>
              <FormControl variant="standard" sx={{ minWidth: 48 }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  color="warning"
                  sx={{
                    background: "#fff",
                    borderRadius: 1,
                    paddingLeft: 1,
                    paddingTop: 0.5,
                    marginLeft: 1,
                    fontSize: 12,
                  }}
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(e.target.value as number)}
                  defaultValue={5}
                  size="small"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </Container>

      <DialogDateEnvoiFacture
        open={openDialogDateEnvoi}
        http={http}
        facturesSelectionnees={facturesAEnvoyer}
        onClose={() => setOpenDialogDateEnvoi(false)}
        onSuccess={() => {
          setSelectedIds([]);
          setReloadList(!reloadList);
        }}
        setOpenSnack={setOpenSnack}
        setAlerteMessage={setAlerteMessage}
        setAlertType={setAlertType}
      />

      <DialogDateValidationFacture
        open={facturesDateValidation.length > 0}
        http={http}
        factures={facturesDateValidation}
        onClose={() => setFacturesDateValidation([])}
        onSuccess={() => {
          setSelectedIds([]);
          setReloadList(!reloadList);
        }}
        setOpenSnack={setOpenSnack}
        setAlerteMessage={setAlerteMessage}
        setAlertType={setAlertType}
      />

      {stackAlert}
      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </>
  );
};

export default TableauDeBordFacturation;
